/**
 * Admin Messages inbox rules: sorting, filtering, and the legacy grouping of raw messages.
 *
 * Pure functions shared by the Messages page and its tests. A "conversation" row has the shape of
 * public.admin_conversations (one row per user): user_id, user_name, user_email, user_role,
 * last_message_at, unread_count, message_count, last_message_preview.
 */

export const SORTS = [
  { key: 'unread_first', label: 'Unread first' },
  { key: 'newest', label: 'Newest' }
]

export const PREVIEW_CHARS = 160

const time = (iso) => {
  const t = new Date(iso || 0).getTime()
  return Number.isFinite(t) ? t : 0
}

const newestFirst = (a, b) => time(b.last_message_at) - time(a.last_message_at)

/** Newest first; with 'unread_first', every conversation that has unread messages comes before the rest. */
export function sortConversations(rows, sort = 'unread_first') {
  const list = [...(rows || [])]
  if (sort === 'unread_first') {
    list.sort((a, b) => (Number((b.unread_count || 0) > 0) - Number((a.unread_count || 0) > 0)) || newestFirst(a, b))
  } else {
    list.sort(newestFirst)
  }
  return list
}

/**
 * Keep the conversations that match the search box and the Unread chip.
 * The box matches name, email and the last message locally; `remoteMatches` is the set of user_ids
 * whose older messages matched the same text on the server, so a hit deep in a thread still surfaces.
 */
/** 'talent' for photographer / videographer accounts, 'client' for everyone else (customers). */
export function audienceOf(c) {
  return ['photographer', 'videographer', 'talent'].includes(String(c?.user_role || '').toLowerCase()) ? 'talent' : 'client'
}

/**
 * `audiences` (optional) keeps only those kinds of conversation: the new console scopes Messages by role (Traci,
 * 23 Sep 2026: Sales sees clients, Talent and Editing see talent, Admin and Billing see both). Omitted = everything,
 * which is how the current console calls it.
 */
export function filterConversations(rows, { query = '', unreadOnly = false, remoteMatches = null, audiences = null } = {}) {
  const q = String(query || '').trim().toLowerCase()
  return (rows || []).filter((c) => {
    if (audiences && !audiences.includes(audienceOf(c))) return false
    if (unreadOnly && !((c.unread_count || 0) > 0)) return false
    if (!q) return true
    const local = [c.user_name, c.user_email, c.last_message_preview]
      .some((v) => String(v || '').toLowerCase().includes(q))
    return local || Boolean(remoteMatches && remoteMatches.has(c.user_id))
  })
}

/** Which conversations each role's Messages page shows (Traci, 23 Sep 2026). */
export const MESSAGE_AUDIENCES = {
  admin: ['client', 'talent'],
  billing: ['client', 'talent'],
  sales: ['client'],
  editing: ['talent'],
  talent_coordinator: ['talent']
}
/** Roles whose Messages page shows only conversations tagged to them (once routing is installed). */
export const ROUTED_ONLY = new Set(['billing', 'editing'])

/**
 * The red badge on the Messages tab (Traci, 28 Sep 2026): unread conversations this role would see on its Messages
 * page. `routeTags` maps conversation user id -> departments it is tagged to, or null when routing is not installed.
 */
export function unreadForRole(rows, role, routeTags = null, started = null) {
  const audiences = MESSAGE_AUDIENCES[role] || ['client', 'talent']
  const routed = ROUTED_ONLY.has(role) && (routeTags || started)
  return filterConversations(rows, { unreadOnly: true, audiences })
    .filter((c) => !routed || isRoutedTo(c.user_id, role, routeTags, started)).length
}

/**
 * Billing and Editing see a conversation tagged to them, or one their department started (Traci's doc, Oct 2026:
 * "only conversations and responses to conversations that their department initiated"). `started` is a Set or null.
 */
export function isRoutedTo(userId, role, routeTags = null, started = null) {
  return Boolean((routeTags?.[userId] || []).includes(role) || started?.has?.(userId))
}

/** The shared unread counts replaced by this person's own (message_reads), when they are known. */
export function withPersonalUnread(rows, unread) {
  if (!unread) return rows || []
  return (rows || []).map((c) => ({ ...c, unread_count: unread[c.user_id] || 0 }))
}

export function summarize(rows) {
  const list = rows || []
  return {
    total: list.length,
    unread: list.filter((c) => (c.unread_count || 0) > 0).length
  }
}

/**
 * Escape a search string for a PostgREST ilike pattern and wrap it in wildcards.
 * `%`, `_` and `\` are LIKE metacharacters; a typed "100%" must match the literal text.
 */
export function ilikePattern(query) {
  const escaped = String(query || '').trim().replace(/[\\%_]/g, (m) => '\\' + m)
  return `%${escaped}%`
}

/**
 * Legacy path, used only until the admin_conversations view exists: group raw messages (newest
 * first) into conversation rows with the same shape and the same unread rule as the view.
 */
export function groupMessages(messages) {
  const byUser = new Map()
  for (const msg of messages || []) {
    const userId = msg.user_id
    const fromUser = msg.sender_id === msg.user_id
    const unread = fromUser && !msg.is_read_admin
    let conv = byUser.get(userId)
    if (!conv) {
      conv = {
        user_id: userId,
        user_name: msg.user?.full_name || null,
        user_email: msg.user?.email || null,
        user_role: msg.user?.role || null,
        last_message_at: msg.created_at,
        unread_count: 0,
        message_count: 0,
        last_message_preview: String(msg.message_text || '').slice(0, PREVIEW_CHARS),
        last_from_user: fromUser
      }
      byUser.set(userId, conv)
    } else if (time(msg.created_at) > time(conv.last_message_at)) {
      conv.last_message_at = msg.created_at
      conv.last_message_preview = String(msg.message_text || '').slice(0, PREVIEW_CHARS)
      conv.last_from_user = fromUser
    }
    conv.message_count += 1
    if (unread) conv.unread_count += 1
  }
  return Array.from(byUser.values())
}
